import { useEffect, useState } from "react";
import { useGetList } from "ra-core";
import {
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";

interface ReportRow {
  sales_id: number | null;
  total: number;
  incoming: number;
  outgoing: number;
  answered: number;
  missed: number;
  talk_seconds: number;
}

const firstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const formatDuration = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
};

export const CallReportPage = () => {
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const load = async () => {
    setLoading(true);
    try {
      const fromIso = new Date(`${from}T00:00:00`).toISOString();
      const toIso = new Date(
        new Date(`${to}T00:00:00`).getTime() + 24 * 3600 * 1000,
      ).toISOString();
      const { data, error } = await getSupabaseClient().rpc("crm_call_report", {
        p_from: fromIso,
        p_to: toIso,
      });
      if (!error && data) setRows(data as ReportRow[]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = rows.reduce(
    (acc, row) => ({
      total: acc.total + row.total,
      incoming: acc.incoming + row.incoming,
      outgoing: acc.outgoing + row.outgoing,
      missed: acc.missed + row.missed,
      talk: acc.talk + row.talk_seconds,
    }),
    { total: 0, incoming: 0, outgoing: 0, missed: 0, talk: 0 },
  );

  const saleName = (salesId: number | null) => {
    if (salesId == null) return "Без ответа (линия)";
    const sale = sales?.find((candidate) => candidate.id === salesId);
    return sale ? `${sale.first_name} ${sale.last_name}`.trim() : `#${salesId}`;
  };

  return (
    <div className="flex flex-col gap-6 pb-8">
      <div className="flex items-end gap-3 flex-wrap">
        <h2 className="text-xl font-semibold mr-auto">Отчёт по звонкам</h2>
        <label className="flex flex-col text-xs text-muted-foreground gap-1">
          С
          <Input
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            className="h-9"
          />
        </label>
        <label className="flex flex-col text-xs text-muted-foreground gap-1">
          По
          <Input
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            className="h-9"
          />
        </label>
        <Button onClick={load} disabled={loading} className="h-9">
          {loading ? "Загрузка..." : "Показать"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Phone} label="Всего звонков" value={totals.total} />
        <StatCard
          icon={PhoneIncoming}
          label="Входящие"
          value={totals.incoming}
        />
        <StatCard
          icon={PhoneOutgoing}
          label="Исходящие"
          value={totals.outgoing}
        />
        <StatCard
          icon={PhoneMissed}
          label="Пропущенные"
          value={totals.missed}
          accent={totals.missed > 0}
        />
        <StatCard
          icon={Timer}
          label="Время на линии"
          value={formatDuration(totals.talk)}
        />
      </div>

      <Card>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 pr-4">Менеджер</th>
                <th className="py-2 pr-4">Всего</th>
                <th className="py-2 pr-4">Входящие</th>
                <th className="py-2 pr-4">Исходящие</th>
                <th className="py-2 pr-4">Отвечено</th>
                <th className="py-2 pr-4">Пропущено</th>
                <th className="py-2">Время на линии</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-6 text-center text-muted-foreground"
                  >
                    Звонков за период нет
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr
                  key={row.sales_id ?? "none"}
                  className="border-b last:border-0"
                >
                  <td className="py-2 pr-4">{saleName(row.sales_id)}</td>
                  <td className="py-2 pr-4">{row.total}</td>
                  <td className="py-2 pr-4">{row.incoming}</td>
                  <td className="py-2 pr-4">{row.outgoing}</td>
                  <td className="py-2 pr-4">{row.answered}</td>
                  <td className="py-2 pr-4">{row.missed}</td>
                  <td className="py-2">{formatDuration(row.talk_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

const StatCard = ({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Phone;
  label: string;
  value: number | string;
  accent?: boolean;
}) => (
  <Card className="py-3">
    <CardContent className="px-4 flex items-center gap-3">
      <Icon
        className={`w-5 h-5 ${accent ? "text-red-500" : "text-muted-foreground"}`}
      />
      <div>
        <p className="text-lg font-semibold leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

CallReportPage.path = "/reports";
